import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, Toggle, LoadingSpinner, Input, Button } from '../../components/ui';
import { useApiQuery, useApiMutation, useInvalidateQueries } from '../../hooks/useApi';
import { configuracionService, ConfiguracionOrganizacion } from '../../services/configuracionService';

/**
 * Controles opcionales de la organización — no todos los clientes los
 * necesitan. Para agregar uno nuevo: 1) columna en configuracion_organizacion
 * (backend), 2) un campo más en ConfiguracionOrganizacion (frontend), 3) una
 * entrada más en este array.
 */
const CONTROLES: {
  key: keyof ConfiguracionOrganizacion;
  labelKey: string;
  descriptionKey: string;
}[] = [
  {
    key: 'combustible_habilitado',
    labelKey: 'admin.settings.fuel_control_label',
    descriptionKey: 'admin.settings.fuel_control_description',
  },
  {
    key: 'ordenes_carga_habilitado',
    labelKey: 'admin.settings.ordenes_carga_label',
    descriptionKey: 'admin.settings.ordenes_carga_description',
  },
];

const BrandingSection: React.FC = () => {
  const { t } = useTranslation();
  const { invalidate } = useInvalidateQueries();
  const [nombreEmpresa, setNombreEmpresa] = useState('');
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [inicializado, setInicializado] = useState(false);

  const { data: branding, isLoading } = useApiQuery(
    ['organizacion-branding'],
    () => configuracionService.obtenerBranding(),
    {
      onSuccess: (data) => {
        if (!inicializado) {
          setNombreEmpresa(data.nombre_empresa);
          setInicializado(true);
        }
      },
    }
  );

  const { mutate: guardar, isLoading: guardando } = useApiMutation(
    () => configuracionService.actualizarBranding(nombreEmpresa, logoFile as File),
    {
      successMessage: t('admin.settings.branding.saved'),
      onSuccess: () => {
        invalidate(['organizacion-branding']);
        setLogoFile(null);
      },
    }
  );

  const puedeGuardar = nombreEmpresa.trim() !== '' && !!logoFile;

  return (
    <Card>
      <div className="p-4 space-y-4">
        <div>
          <h3 className="text-base font-semibold text-gray-900">{t('admin.settings.branding.title')}</h3>
          <p className="text-sm text-gray-600">{t('admin.settings.branding.description')}</p>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-4">
            <LoadingSpinner />
          </div>
        ) : (
          <>
            <Input
              label={t('admin.settings.branding.nombre_empresa')}
              value={nombreEmpresa}
              onChange={(e) => setNombreEmpresa(e.target.value)}
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('admin.settings.branding.logo')}
              </label>
              <div className="flex items-center gap-4">
                {logoFile ? (
                  <img src={URL.createObjectURL(logoFile)} alt="logo" className="h-16 object-contain border border-gray-200 rounded bg-white p-1" />
                ) : branding?.tiene_logo ? (
                  <span className="text-sm text-gray-500">{t('admin.settings.branding.tiene_logo')}</span>
                ) : (
                  <span className="text-sm text-gray-400">{t('admin.settings.branding.sin_logo')}</span>
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setLogoFile(e.target.files?.[0] || null)}
                  className="text-sm"
                />
              </div>
            </div>

            <div className="flex justify-end">
              <Button disabled={!puedeGuardar} loading={guardando} onClick={() => guardar(undefined)}>
                {t('common.save')}
              </Button>
            </div>
          </>
        )}
      </div>
    </Card>
  );
};

const ConfiguracionEmpresaView: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { invalidate } = useInvalidateQueries();

  const { data: config, isLoading } = useApiQuery(
    ['configuracion-organizacion'],
    () => configuracionService.obtener()
  );

  const { mutate: actualizar, isLoading: guardando } = useApiMutation(
    (datos: Partial<ConfiguracionOrganizacion>) => configuracionService.actualizar(datos),
    {
      successMessage: t('admin.settings.saved'),
      onSuccess: () => {
        invalidate(['configuracion-organizacion']);
        invalidate(['combustible-configuracion']);
      },
    }
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('admin.settings.title')}</h1>
          <p className="text-gray-600 mt-1">{t('admin.settings.subtitle')}</p>
        </div>
        <button
          onClick={() => navigate('/admin')}
          className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 flex items-center space-x-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          <span>{t('common.back_to_admin')}</span>
        </button>
      </div>

      <Card>
        {isLoading || !config ? (
          <div className="flex justify-center py-8">
            <LoadingSpinner />
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {CONTROLES.map((control) => (
              <div key={control.key} className="py-4 first:pt-0 last:pb-0">
                <Toggle
                  checked={!!config[control.key]}
                  disabled={guardando}
                  label={t(control.labelKey)}
                  description={t(control.descriptionKey)}
                  onChange={(checked) => actualizar({ [control.key]: checked })}
                />
              </div>
            ))}
          </div>
        )}
      </Card>

      <BrandingSection />
    </div>
  );
};

export default ConfiguracionEmpresaView;
